import dayjs from 'dayjs'
import { t } from 'i18next'
import { Button } from 'primereact/button'
import { Messages } from 'primereact/messages'
import React, { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { CompanyForm } from './CompanyForm'
import { API_COMPANY_BY_ID } from '../../../../../constants'
import { Company } from '../../../../../model/company.model'
import { RequestMethod } from '../../../../../model/enums/enums'
import { updateCompany } from '../../../../../redux/basic/company/companyActions'
import { useAppDispatch } from '../../../../../redux/hooks'
import { AuthRoutes } from '../../../../../router/routes'
import { handleRequest } from '../../../../../services/api/base/handleRequest'
import { BackButton } from '../../../../util/button/BackButton'

export const UpdateCompanyEntry = () => {
  const location = useLocation()

  const [company, setCompany] = useState<Company | undefined>(undefined)

  const navigate = useNavigate()

  const dispatcher = useAppDispatch()
  const msg = useRef<Messages>(null)

  const loadCompany = async (companyId: string) => {
    const id = parseInt(companyId)
    if (isNaN(id)) {
      console.warn(
        '[Update Company] CompanyId is not a number. ' +
          'Can not load the company to update, redirecting to Company overview.'
      )
      navigate(AuthRoutes.company)
      return
    }
    const comp = await handleRequest<Company>(
      API_COMPANY_BY_ID(id),
      RequestMethod.GET
    )
    // we receive the value as null but can not send it as null
    const compCopy: any = { ...comp.data }
    // remove all undefined or empty keys
    Object.keys(compCopy).forEach((key) => {
      if (compCopy[key] === null) {
        compCopy[key] = undefined
      }
    })

    if (compCopy?.company_id === undefined || compCopy?.company_id === 0) {
      console.warn(`[Update Company] 
				No company found with id '${id}'. Can not load the company to update, redirecting to company overview.`)
      navigate(AuthRoutes.company)
    }

    setCompany(compCopy)
  }

  useEffect(() => {
    if (location) {
      // the location is at this point ...Company/<companyId> and we want to extract the Company id.
      // Reason: If someone sends this link we want to display the data of this url
      const n = location.pathname.lastIndexOf('/')
      const companyId = location.pathname.substring(n + 1)

      if (!companyId) {
        console.warn(
          '[Update Company] CompanyId not defined, redirecting to Company overview.'
        )
        navigate(AuthRoutes.company)
        return
      }

      loadCompany(companyId)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  const formMethods = useForm({
    reValidateMode: 'onSubmit',
    mode: 'onSubmit',
    shouldFocusError: true,
  })

  useEffect(() => {
    formMethods.reset(company)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company])

  const cancelUpdate = () => {
    console.log(
      '[Update Company] Return to the previews view without updating the company.'
    )
    navigate(-1)
  }
  const handleUpdate = async (updatedCompany: Company) => {
    msg.current?.clear()
    const companyId = updatedCompany.company_id
    if (!companyId) {
      console.warn(
        '[Update Company] Company ID not defined. Can not update the company.'
      )
      return
    }

    // format timestamp to mysql valid date
    if (updatedCompany.founding_date) {
      const validDate = dayjs(updatedCompany.founding_date).format(
        'YYYY-MM-DD'
      )
      updatedCompany.founding_date = validDate !== null ? validDate : undefined
    }

    if (updatedCompany.founding_date === null) {
      updatedCompany.founding_date = undefined
    }

    // empty string will cause error on backend - empty string is set, when iban is invalid
    if (updatedCompany.iban === '') {
      updatedCompany.iban = undefined
    }

    const result = await handleRequest<Company>(
      API_COMPANY_BY_ID(companyId),
      RequestMethod.PUT,
      updatedCompany
    )

    if (result.data && !result.error) {
      console.log(
        `[Update Company] Successfully updated the company with id ${companyId}.`
      )
      dispatcher(updateCompany(result.data))
      msg?.current?.show({
        severity: 'success',
        sticky: false,
        life: 8000,
        closable: true,
        detail: t<string>('company.message.update.success'),
      })
      return
    }
    // todo msg
    msg?.current?.show({
      severity: 'error',
      sticky: true,
      life: 8000,
      closable: true,
      detail:
        t<string>('company.message.update.fail') +
        ' (' +
        result.error?.message +
        ')',
    })
    console.log(
      `[Update Company] Failed to update company with id ${companyId}.`,
      JSON.stringify(result)
    )
  }

  return (
    <div className={'card ml-2 pr-2'}>
      <div className={'field grid'}>
        <BackButton translationKey={'company.back'} />
      </div>

      <div className={'field grid'}>
        <label
          className={'col-12 mb-2 md:col-2 md:mb-0'}
          htmlFor={'company_id'}
        >
          {t<string>('company.company_id')}
        </label>
        <span id={'company_id'}>
          {company?.company_id ? company?.company_id : ''}
        </span>
      </div>
      <FormProvider {...formMethods}>
        <form
          onSubmit={formMethods.handleSubmit(handleUpdate)}
          autoComplete={'off'}
        >
          {CompanyForm(formMethods)}
          <div className={'md:col-12'}>
            <Button
              label={t<string>('core.update')}
              icon={'fa-solid fa-floppy-disk'}
              type={'submit'}
              className={'float-right mb-4'}
            />
            <Button
              label={t<string>('core.cancel')}
              className={'p-button-text p-button-danger mr-2 float-right'}
              icon={'fa-solid fa-xmark'}
              onClick={() => cancelUpdate()}
              type={'button'}
            />
          </div>
        </form>
      </FormProvider>
      <Messages ref={msg} className={'mt-6'} />
    </div>
  )
}